import { useState } from 'react';
import {
  ConfigCard,
  ConfigCheckbox,
  ConfigDraftJsonDrawer,
  ConfigEditorSection,
  ConfigEditorShell,
  ConfigField,
  ConfigKeyValueEditor,
  ConfigRepeatableRows,
  ConfigSegmentedControl,
  ConfigSelect,
  ConfigTextarea,
  ConfigTextInput,
  ConfigToggle,
  SecretRefSelectorPlaceholder,
  useConfigDraftEditor,
  type ConfigKeyValueEntry,
} from './ConfigEditor';
import { useDirtyDraftGuard, type DirtyDraftConfirm } from './guard';

type HarnessConfig = {
  displayName: string;
  description: string;
  provider: string;
  enabled: boolean;
  requiresApproval: boolean;
  rolloutMode: string;
  tags: Array<{ value: string }>;
  labels: ConfigKeyValueEntry[];
  secretRef: string;
};

const emptyHarnessConfig: HarnessConfig = {
  displayName: '',
  description: '',
  provider: 'codex',
  enabled: false,
  requiresApproval: true,
  rolloutMode: 'draft',
  tags: [],
  labels: [],
  secretRef: '',
};

const publishedHarnessConfig: HarnessConfig = {
  displayName: 'Shared Config Editor',
  description: 'Published fallback used when a current draft is not present.',
  provider: 'codex',
  enabled: true,
  requiresApproval: true,
  rolloutMode: 'published',
  tags: [{ value: 'console-v2' }],
  labels: [{ key: 'owner', value: 'agent-infra' }],
  secretRef: 'secret/config-editor-reference',
};

const currentDraftHarnessConfig: HarnessConfig = {
  ...publishedHarnessConfig,
  description: 'Draft value wins over published/current fallback.',
  rolloutMode: 'canary',
  labels: [...publishedHarnessConfig.labels, { key: 'foundation', value: 'agif2-351' }],
};

export function ConfigEditorHarness({
  currentDraft = currentDraftHarnessConfig,
  published = publishedHarnessConfig,
  current = publishedHarnessConfig,
  confirmLeave,
  initialDraftJsonOpen = false,
}: {
  currentDraft?: HarnessConfig | null;
  published?: HarnessConfig | null;
  current?: HarnessConfig | null;
  confirmLeave?: DirtyDraftConfirm;
  initialDraftJsonOpen?: boolean;
}) {
  const editor = useConfigDraftEditor<HarnessConfig>({
    currentDraft,
    published,
    current,
    empty: emptyHarnessConfig,
    onSaveDraft: values => values,
  });
  const [draftJsonOpen, setDraftJsonOpen] = useState(initialDraftJsonOpen);
  const leaveGuard = useDirtyDraftGuard({ dirty: editor.dirty, confirm: confirmLeave });
  const values = editor.values;

  return (
    <div className="config-editor-harness page-stack">
      <ConfigEditorShell
        title="Console v2 shared config editor harness"
        description="Low-risk harness for Persona, Prompt, Skill, MCP, and System pages to reuse without migrating resource pages in this PR."
        source={editor.source}
        dirty={editor.dirty}
        onDiscardChanges={editor.discardChanges}
        onOpenDraftJson={() => setDraftJsonOpen(true)}
        onSaveDraft={() => void editor.saveDraft()}
      >
        <ConfigEditorSection title="Basics" description="GCP-style section, field labels, helper text, required markers, and validation errors.">
          <div className="form-grid">
            <ConfigField id="config-display-name" label="Display name" required helperText="Used as the human-readable resource label.">
              <ConfigTextInput id="config-display-name" value={values.displayName} onChange={displayName => editor.setField('displayName', displayName)} />
            </ConfigField>
            <ConfigField id="config-provider" label="Provider" helperText="Select control-plane ownership without changing product semantics.">
              <ConfigSelect
                id="config-provider"
                value={values.provider}
                onChange={provider => editor.setField('provider', provider)}
                options={[
                  { value: 'codex', label: 'Codex' },
                  { value: 'relay', label: 'Relay' },
                  { value: 'system', label: 'System' },
                ]}
              />
            </ConfigField>
            <ConfigField id="config-description" label="Description" helperText="Textarea control shared by long-form config resources.">
              <ConfigTextarea id="config-description" value={values.description} onChange={description => editor.setField('description', description)} />
            </ConfigField>
            <ConfigField label="Secret reference" helperText="Placeholder selector proves refs can be wired without exposing values.">
              <SecretRefSelectorPlaceholder
                value={values.secretRef}
                onChange={secretRef => editor.setField('secretRef', secretRef)}
                options={[
                  { id: 'secret/config-editor-reference', label: 'Config editor reference' },
                  { id: 'secret/platform-webhook', label: 'Platform webhook reference' },
                ]}
              />
            </ConfigField>
          </div>
        </ConfigEditorSection>

        <ConfigEditorSection title="Controls" description="Toggle, checkbox, segmented control, repeatable rows, and key/value editor.">
          <div className="content-grid">
            <ConfigCard title="State">
              <div className="page-stack">
                <ConfigField label="Enabled">
                  <ConfigToggle checked={values.enabled} onChange={enabled => editor.setField('enabled', enabled)} label="Enabled" />
                </ConfigField>
                <ConfigField label="Approval guard">
                  <ConfigCheckbox checked={values.requiresApproval} onChange={requiresApproval => editor.setField('requiresApproval', requiresApproval)} label="Require typed approval before publish" />
                </ConfigField>
                <ConfigField label="Rollout mode">
                  <ConfigSegmentedControl
                    label="Rollout mode"
                    value={values.rolloutMode}
                    onChange={rolloutMode => editor.setField('rolloutMode', rolloutMode)}
                    options={[
                      { value: 'draft', label: 'Draft' },
                      { value: 'canary', label: 'Canary' },
                      { value: 'published', label: 'Published' },
                    ]}
                  />
                </ConfigField>
              </div>
            </ConfigCard>
            <ConfigCard title="Lists and maps">
              <div className="page-stack">
                <ConfigField label="Tags">
                  <ConfigRepeatableRows
                    items={values.tags}
                    onChange={tags => editor.setField('tags', tags)}
                    createItem={() => ({ value: '' })}
                    addLabel="Add tag"
                    renderItem={({ item, index, updateItem, removeItem }) => (
                      <div className="config-key-value-row">
                        <ConfigTextInput aria-label={`Tag ${index + 1}`} value={item.value} onChange={value => updateItem({ value })} placeholder="Tag" />
                        <button className="btn btn-compact btn-subtle" type="button" onClick={removeItem}>Remove</button>
                      </div>
                    )}
                  />
                </ConfigField>
                <ConfigField label="Labels">
                  <ConfigKeyValueEditor entries={values.labels} onChange={labels => editor.setField('labels', labels)} />
                </ConfigField>
              </div>
            </ConfigCard>
          </div>
        </ConfigEditorSection>
      </ConfigEditorShell>
      <ConfigDraftJsonDrawer
        open={draftJsonOpen}
        onClose={() => leaveGuard.guardDrawerClose(() => setDraftJsonOpen(false))}
        draft={values}
        published={editor.publishedValue}
      />
    </div>
  );
}
